/**
 * 极简 Hash 路由
 * ------------------------------------------------------------
 * 用 hash 而不是 History API，好处：静态托管（GitHub Pages / 本地 file 预览）
 * 完全不需要服务器重写规则，部署零配置。
 *
 * 路由表：{ '#/home': renderFn, '#/practice': ... }
 * 每个 renderFn(mountEl, params) 负责渲染整页。
 */
import { emit, EVENTS } from '../core/events.js';

const routes = new Map();
let notFound = null;
let currentCleanup = null;
let mount = null;

/**
 * 注册路由
 * @param {string} pattern '#/practice/:module' 支持 :param 占位
 * @param {(mount:HTMLElement, params:object)=>any} handler 可返回 cleanup 函数
 */
export function route(pattern, handler) {
  routes.set(pattern, handler);
}

export function setNotFound(handler) {
  notFound = handler;
}

/** 解析 '#/practice/word?unit=u1' → { path:'#/practice/word', query:{unit:'u1'} } */
export function parseHash(hash = location.hash || '#/home') {
  const [path, qs = ''] = hash.split('?');
  const query = {};
  new URLSearchParams(qs).forEach((v, k) => { query[k] = v; });
  return { path: path || '#/home', query };
}

function matchPath(path) {
  if (routes.has(path)) return { handler: routes.get(path), params: {} };
  for (const [pattern, handler] of routes) {
    if (!pattern.includes(':')) continue;
    const pSeg = pattern.split('/');
    const aSeg = path.split('/');
    if (pSeg.length !== aSeg.length) continue;
    const params = {};
    let ok = true;
    for (let i = 0; i < pSeg.length; i++) {
      if (pSeg[i].startsWith(':')) params[pSeg[i].slice(1)] = decodeURIComponent(aSeg[i]);
      else if (pSeg[i] !== aSeg[i]) { ok = false; break; }
    }
    if (ok) return { handler, params };
  }
  return null;
}

export async function render() {
  if (!mount) return;
  const { path, query } = parseHash();
  const hit = matchPath(path);
  const handler = hit?.handler || notFound;
  if (!handler) return;

  // 清理上一页（定时器、事件监听）
  try { currentCleanup?.(); } catch (err) { console.warn('[router] cleanup 异常', err); }
  currentCleanup = null;

  // 高亮侧边栏
  const base = path.split('/')[1] || 'home';
  document.querySelectorAll('[data-nav]').forEach(a => {
    a.classList.toggle('active', a.dataset.nav === base);
  });

  mount.innerHTML = '<div class="page-loading">加载中…</div>';
  try {
    const cleanup = await handler(mount, { ...hit?.params, ...query });
    currentCleanup = typeof cleanup === 'function' ? cleanup : null;
  } catch (err) {
    console.error('[router] 页面渲染失败', err);
    mount.innerHTML = `<div class="empty"><span class="icon">⚠️</span>
      <p><strong>页面加载失败</strong></p>
      <p class="muted">${String(err.message || err)}</p>
      <button class="btn btn-primary" onclick="location.reload()">刷新页面</button></div>`;
  }

  window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  emit(EVENTS.ROUTE_CHANGED, { path, query });
}

/** 跳转 */
export function navigate(path, query = {}) {
  const qs = new URLSearchParams(query).toString();
  location.hash = qs ? `${path}?${qs}` : path;
}

export function startRouter(mountEl) {
  mount = mountEl;
  window.addEventListener('hashchange', render);
  if (!location.hash) location.hash = '#/home';
  else render();
}

export function currentPath() {
  return parseHash().path;
}
